import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
let gsiPromise = null;

function loadGsi() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Failed to load Google"));
    document.head.appendChild(s);
  });
  return gsiPromise;
}

// Renders the official "Continue with Google" button (web Google Identity Services).
export function GoogleSignInButton({ redirectTo = "/", referralCode }) {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const refCodeRef = useRef(referralCode);
  refCodeRef.current = referralCode;

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;
    loadGsi()
      .then(() => {
        if (cancelled || !ref.current) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: async (resp) => {
            try {
              await loginWithGoogle(resp.credential, refCodeRef.current);
              navigate(redirectTo);
            } catch (err) {
              toast.error(err.message || "Google sign-in failed");
            }
          },
        });
        window.google.accounts.id.renderButton(ref.current, {
          type: "standard", theme: "outline", size: "large",
          text: "continue_with", shape: "pill", logo_alignment: "left", width: 320,
        });
        setReady(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loginWithGoogle, navigate, redirectTo]);

  if (!CLIENT_ID) return null;

  return (
    <div style={{ marginTop: 18 }} data-testid="google-signin">
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 14px" }}>
        <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
        <span className="ss-muted" style={{ fontSize: 12, fontWeight: 700 }}>OR</span>
        <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div ref={ref} />
        {!ready && <span className="ss-muted" style={{ fontSize: 13 }}>Loading Google…</span>}
      </div>
    </div>
  );
}
